// ========================================
// Formaciones Management - Details & Subjects
// ========================================

// Colors go into style attributes: anything that is not a hex color falls back (F9, 2026-10-05).
function formationDetailsColor(value, fallback) {
    return typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value) ? value : fallback;
}

Object.assign(Formaciones, {

    // Ficha de una formación, diseño A (2026-10-26): datos en etiquetas, cuatro cifras (alumnos, clases,
    // horas dadas y avisos) y una tabla de asignaturas; al pulsar una se despliegan sus clases.
    // Estilos: assets/css/views/formaciones.css (.fd-*).
    renderDetails(formationId) {
        const formation = DataManager.getFormation(formationId);
        if (!formation) {
            return `
                <div class="view-container dnr-proposal fd-view">
                    <p class="fd-empty">Formación no encontrada</p>
                </div>
            `;
        }

        const subjects = DataManager.getSubjectsByFormation(formationId);

        // This formation's classes of the current course, loaded on demand.
        if (typeof FileStorage !== 'undefined' && FileStorage.needClasses) {
            const hash = App.currentRoute();
            FileStorage.needClasses({ ...FileStorage.classBlockRange(FileStorage.classBlock(new Date())), formationId }, () => {
                if (App.currentRoute() === hash) App.navigate(hash);
            });
        }

        const esc = value => Utils.sanitizeHTML(String(value));
        const subjectSummary = this.currentClassSummary('subject');
        const formationSummary = this.currentClassSummary('formation');
        const rows = subjects.map(subject => {
            const classes = DataManager.getClassesBySubject(subject.id);
            const summary = subjectSummary?.get(String(subject.id));
            const done = Math.round((summary ? summary.hours : classes.reduce((sum, c) => sum + Utils.calculateDuration(c.startTime, c.endTime), 0)) * 10) / 10;
            const professor = subject.professorId ? DataManager.getProfessor(subject.professorId) : null;
            return { subject, classes, done, planned: Number(subject.totalHours) || 0, count: summary ? summary.count : classes.length,
                professor: professor ? `${professor.firstName || ''} ${professor.lastName || ''}`.trim() : '' };
        });

        const students = this.studentsByFormation().get(String(formation.id))?.size || 0;
        const classCount = formationSummary ? (formationSummary.get(String(formation.id))?.count || 0) : rows.reduce((sum, r) => sum + r.count, 0);
        const done = Math.round(rows.reduce((sum, r) => sum + r.done, 0) * 10) / 10;
        const planned = rows.reduce((sum, r) => sum + r.planned, 0);
        const warnings = rows.filter(r => !r.professor).map(r => `${r.subject.name} sin profesor`)
            .concat(rows.filter(r => r.planned && r.done > r.planned).map(r => `${r.subject.name} se pasa`));

        const hasPlan = formation.studyPlanId && typeof StudyPlanService !== 'undefined';
        const plan = hasPlan ? StudyPlanService.label(formation.studyPlanId) : '';
        const years = hasPlan ? StudyPlanService.years(formation.studyPlanId, formation.type) : FormationTypeService.years(formation.type);
        const year = formation.academicYear ? (years.find(y => String(y.id) === String(formation.academicYear))?.name || `${formation.academicYear}º`) : '';
        const typeColor = this.typeColor(formation.type);
        const tags = [
            `<span class="fd-tag is-type" style="--fd-color: ${typeColor}">${esc(this.getFormationTypeLabel(formation.type))}</span>`,
            `<span class="fd-tag">${plan ? `Plan: ${esc(plan)}` : 'Sin plan de estudios'}</span>`,
            year ? `<span class="fd-tag">${esc(year)} curso</span>` : '',
            formation.defaultStartTime && formation.defaultEndTime ? `<span class="fd-tag">${esc(formation.defaultStartTime)}–${esc(formation.defaultEndTime)}</span>` : '',
            formation.commercialName ? `<span class="fd-tag is-amber">Comercial: ${esc(formation.commercialName)}</span>` : ''
        ].join('');
        const stat = (label, value, sub, tone = '') => `
            <div class="fd-stat ${tone}"><span class="fd-stat-l">${label}</span><span class="fd-stat-n">${value}</span><span class="fd-stat-s" title="${esc(sub)}">${esc(sub)}</span></div>`;

        return `
            <div class="view-container dnr-proposal fd-view">
                <header class="fd-head">
                    <button type="button" class="fd-back" aria-label="Volver a Formaciones" title="Volver a Formaciones" ${UIEvents.attr("click", function(event,captured){Formaciones.currentFormationId=null;App.navigate('formaciones');}, [])}>
                        <i class="fas fa-arrow-left"></i>
                    </button>
                    <div class="fd-head-info">
                        <h1 class="fd-title">${esc(formation.name)}</h1>
                        <div class="fd-tags">${tags}</div>
                        ${formation.description ? `<p class="fd-desc">${esc(formation.description)}</p>` : ''}
                    </div>
                    <div class="fd-head-actions">
                        <button type="button" class="fd-btn" ${UIEvents.attr("click", function(event,captured){Formaciones.showEditForm(String(captured[0]));}, [formationId])}>Editar formación</button>
                        <button type="button" class="fd-btn is-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.showAddSubjectForm(String(captured[0]));}, [formationId])}><i class="fas fa-plus"></i> Añadir asignatura</button>
                    </div>
                </header>

                <div class="fd-stats">
                    ${stat('Alumnos', students, students === 1 ? 'activo en esta formación' : 'activos en esta formación')}
                    ${stat('Clases este curso', classCount, `en ${rows.filter(r => r.count).length} de ${rows.length} asignaturas`, 'is-teal')}
                    ${stat('Horas dadas', planned ? `${done} / ${planned}` : done, planned ? `${Math.round(done / planned * 100)} % de lo previsto` : 'sin horas previstas', 'is-teal')}
                    ${stat('Avisos', warnings.length, warnings.length ? warnings.slice(0, 2).join(' · ') + (warnings.length > 2 ? ` · y ${warnings.length - 2} más` : '') : 'todo en orden', warnings.length ? 'is-red' : '')}
                </div>

                <section class="fd-card">
                    <div class="fd-card-head">
                        <h2 class="fd-h2">Asignaturas · ${rows.length}</h2>
                        ${rows.length ? '<span class="fd-hint">Pulsa una asignatura para ver sus clases</span>' : ''}
                    </div>
                    ${rows.length ? `
                    <div class="fd-table-wrap">
                        <table class="fd-table">
                            <thead><tr><th scope="col">Asignatura</th><th scope="col">Profesor</th><th scope="col">Horario</th><th scope="col">Horas dadas / previstas</th><th scope="col">Clases</th><th scope="col"><span class="sr-only">Acciones</span></th></tr></thead>
                            <tbody>${rows.map(r => this.renderSubjectRow(r, formationId)).join('')}</tbody>
                        </table>
                    </div>` : '<p class="fd-empty">No hay asignaturas en esta formación. Pulsa «Añadir asignatura» para empezar.</p>'}
                </section>
            </div>
        `;
    },

    toggleDetailsSubject(subjectId) {
        this._fdOpenSubject = this._fdOpenSubject === subjectId ? null : subjectId;
        this._fdShowPast = false;
        App.render();
    },

    showPastSubjectClasses() {
        this._fdShowPast = true;
        App.render();
    },

    renderSubjectRow(row, formationId) {
        const { subject, classes, done, planned, count, professor } = row;
        const esc = value => Utils.sanitizeHTML(String(value));
        const id = String(subject.id);
        const open = this._fdOpenSubject === id;
        const color = formationDetailsColor(subject.color, '#64748b');
        const state = planned && done > planned ? 'is-over' : planned && done >= planned ? 'is-done' : 'is-going';
        const pct = planned ? Math.min(100, Math.round(done / planned * 100)) : 0;
        const time = subject.defaultStartTime && subject.defaultEndTime ? `${subject.defaultStartTime}–${subject.defaultEndTime}` : '—';
        const action = (icon, label, handler, extra = '') => `
            <button type="button" class="fd-icon ${extra}" aria-label="${label}" title="${label}" ${UIEvents.attr("click", handler, [id, String(formationId)])}><i class="fas ${icon}"></i></button>`;

        let details = '';
        if (open) {
            const today = new Date().toISOString().slice(0, 10);
            const sorted = [...classes].sort((a, b) => String(a.startDate || '').localeCompare(String(b.startDate || '')) || String(a.startTime).localeCompare(String(b.startTime)));
            const upcoming = sorted.filter(c => !c.startDate || c.startDate >= today);
            const past = sorted.length - upcoming.length;
            const list = this._fdShowPast ? sorted : upcoming;
            const day = c => {
                const date = c.startDate ? new Date(`${c.startDate}T12:00:00`) : null;
                return date && !isNaN(date) ? date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : Utils.getDayName(c.dayOfWeek);
            };
            details = `
                <tr class="fd-classes-row"><td colspan="6">
                    <div class="fd-classes">
                        <span class="fd-classes-l">${this._fdShowPast ? 'Clases de este curso' : 'Próximas clases'}</span>
                        ${list.length ? list.map(c => `
                            <span class="fd-class">
                                <b>${esc(day(c))}</b><span>${esc(c.startTime)}–${esc(c.endTime)}</span>${c.classroom ? `<span class="fd-class-room">${esc(c.classroom)}</span>` : ''}
                                <button type="button" class="fd-class-del" aria-label="Eliminar clase" title="Eliminar clase" ${UIEvents.attr("click", function(event,captured){Formaciones.deleteClass(String(captured[0]));}, [c.id])}><i class="fas fa-trash-can"></i></button>
                            </span>`).join('') : `<span class="fd-hint">${sorted.length ? 'No quedan clases por dar este curso.' : 'Aún no tiene clases programadas este curso.'}</span>`}
                        ${!this._fdShowPast && past ? `<button type="button" class="fd-link" ${UIEvents.attr("click", function(event,captured){Formaciones.showPastSubjectClasses();}, [])}>Ver también las ${past} ya dadas</button>` : ''}
                    </div>
                </td></tr>`;
        }

        return `
            <tr class="fd-row ${open ? 'is-open' : ''}" data-subject-id="${esc(id)}">
                <td>
                    <button type="button" class="fd-subject" aria-expanded="${open}" style="--fd-color: ${color}" ${UIEvents.attr("click", function(event,captured){Formaciones.toggleDetailsSubject(String(captured[0]));}, [id])}>
                        <span class="fd-dot" aria-hidden="true"></span><span class="fd-subject-name">${esc(subject.name)}</span><span class="fd-type">${esc(subject.type || 'COMÚN')}</span>
                    </button>
                </td>
                <td class="${professor ? 'fd-prof' : 'fd-prof is-missing'}">${professor ? esc(professor) : 'Sin profesor'}</td>
                <td class="fd-mono">${esc(time)}</td>
                <td>
                    <div class="fd-hours ${state}">
                        <span class="fd-mono">${planned ? `${done} / ${planned} h` : `${done} h`}</span>
                        ${planned ? `<span class="fd-meter" aria-hidden="true"><i style="width: ${state === 'is-over' ? 100 : pct}%"></i></span>` : ''}
                    </div>
                </td>
                <td class="fd-mono">${count}</td>
                <td>
                    <div class="fd-actions">
                        ${action('fa-calendar-plus', 'Programar clases', function(event,captured){Scheduling.showScheduleForm(String(captured[0]),String(captured[1]));})}
                        ${action('fa-copy', 'Copiar a otra formación', function(event,captured){Formaciones.showCopySubjectForm(String(captured[0]));})}
                        ${action('fa-pen', 'Editar asignatura', function(event,captured){Formaciones.showEditSubjectForm(String(captured[0]));})}
                        ${action('fa-trash-can', 'Eliminar asignatura', function(event,captured){Formaciones.deleteSubject(String(captured[0]));}, 'is-danger')}
                    </div>
                </td>
            </tr>${details}`;
    },

    showAddSubjectForm(formationId) {
        if (!AuthManager.requireModuleManage("plan-estudios",["admin","coordinator"])) return;
        const formation = DataManager.getFormation(formationId);
        const professors = DataManager.getProfessors();
        if (!formation) return;

        // Detect current year and get suggestions from previous courses
        const currentYear = formation.academicYear ? String(formation.academicYear) : '1';
        const prevYears = [];
        if (currentYear === '2') prevYears.push('1');
        else if (currentYear === '3') prevYears.push('1', '2');

        let suggestedSubjects = [];
        if (prevYears.length > 0) {
            const allFormations = DataManager.getFormations();
            const prevFormations = allFormations.filter(f => 
                f.type === formation.type &&
                prevYears.includes(String(f.academicYear))
            );
            const prevFormIds = new Set(prevFormations.map(f => String(f.id)));
            const allSubjects = DataManager.getSubjects();

            const seen = new Map();
            allSubjects.forEach(s => {
                if (!prevFormIds.has(String(s.formationId))) return;
                if (['VAC', 'EXT'].includes(s.type?.toUpperCase())) return;
                const normKey = Utils.normalizeString(s.name.trim());
                if (!seen.has(normKey)) {
                    const f = allFormations.find(form => String(form.id) === String(s.formationId));
                    seen.set(normKey, {
                        id: s.id,
                        name: s.name.trim(),
                        type: s.type || 'COMÚN',
                        studyBlock: s.studyBlock || 'OTROS',
                        color: s.color || '#4169E1',
                        professorId: s.professorId || '',
                        year: f ? f.academicYear : '1',
                        formationName: f ? f.name : ''
                    });
                } else if (/[áéíóúÁÉÍÓÚñÑ]/.test(s.name) && !/[áéíóúÁÉÍÓÚñÑ]/.test(seen.get(normKey).name)) {
                    seen.get(normKey).name = s.name.trim();
                }
            });

            suggestedSubjects = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name, 'es'));
        }

        const content = `
            <form id="subject-form" class="dnr-proposal fmtd-form-dnr" ${UIEvents.attr("submit", function(event,captured){Formaciones.handleAddSubject(event,String(captured[0]));}, [formationId])}>
                <p style="color: var(--dnr-ink-mute); margin-bottom: 1rem;">
                    Añadir asignatura a: <strong style="color: var(--dnr-ink);">${Utils.sanitizeHTML(formation.name)}</strong>
                    ${this.getFormationTypeBadge(formation.type)}
                </p>

                ${suggestedSubjects.length > 0 ? `
                    <div class="fmtd-suggestion-box">
                        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px;">
                            <label style="font-size: 0.8rem; font-weight: 800; color: var(--dnr-teal-700); display: flex; align-items: center; gap: 6px; margin: 0;">
                                <i class="fas fa-wand-magic-sparkles"></i>
                                ¿Es continuación de una asignatura de ${currentYear === '2' ? '1º Curso' : '1º o 2º Curso'}?
                            </label>
                            <span style="font-size: 0.7rem; color: var(--dnr-teal-700); font-weight: 600;">Opcional</span>
                        </div>
                        <select id="continuation-subject-select" class="form-select" style="font-size: 0.82rem; height: 35px; padding: 4px 10px;" ${UIEvents.attr("change", function(event,captured){Formaciones.applySubjectSuggestion(this.value);}, [])}>
                            <option value="">-- Elige una asignatura previa para autocompletar --</option>
                            ${suggestedSubjects.map(s => {
                                const payload = encodeURIComponent(JSON.stringify(s));
                                return `<option value="${payload}">${Utils.sanitizeHTML(s.name)} (${s.year ? s.year + 'º Curso' : 'Previo'} · ${s.type})</option>`;
                            }).join('')}
                        </select>
                        <div id="continuation-applied-notice" style="display: none; font-size: 0.74rem; color: var(--dnr-success); margin-top: 6px; font-weight: 700; align-items: center; gap: 5px;">
                            <i class="fas fa-check-circle"></i> Asignatura vinculada como continuación.
                        </div>
                    </div>
                ` : ''}

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Nombre de la Asignatura *</label>
                        <input type="text" class="form-input" name="name" id="new-subject-name" list="suggested-subjects-datalist" required placeholder="Ej: Dirección Cinematográfica">
                        ${suggestedSubjects.length > 0 ? `
                            <datalist id="suggested-subjects-datalist">
                                ${suggestedSubjects.map(s => `<option value="${Utils.sanitizeHTML(s.name)}">${s.year ? s.year + 'º Curso' : ''} (${Utils.sanitizeHTML(s.type)})</option>`).join('')}
                            </datalist>
                        ` : ''}
                    </div>
                    <div>
                        <label class="form-label">Tipo *</label>
                        <select class="form-select" name="type" required ${UIEvents.attr("change", function(event,captured){Formaciones.handleSubjectTypeChange(this);}, [])}>
                            <option value="COMÚN">COMÚN</option>
                            <option value="DPT">DPT</option>
                            <option value="EXT">EXT</option>
                            <option value="PRACT">PRACT</option>
                            <option value="VAC">VAC</option>
                        </select>
                    </div>
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Bloque de Estudios</label>
                        <select class="form-select" name="studyBlock">
                            <option value="">-- Sin asignar --</option>
                            ${this.getBlocksForCategory(formation ? formation.type : null).map(b => `<option value="${Utils.sanitizeHTML(b.id)}">${Utils.sanitizeHTML(this.blockLabel(b))}</option>`).join('')}
                        </select>
                    </div>
                    <div>
                        <label class="form-label">Profesor Asignado</label>
                        <select class="form-select" name="professorId" id="new-subject-professor">
                            <option value="">Sin profesor asignado</option>
                            ${professors.map(prof => `
                                <option value="${Utils.sanitizeHTML(prof.id)}">
                                    ${Utils.sanitizeHTML(prof.firstName)} ${Utils.sanitizeHTML(prof.lastName)} - ${StaffPay.isFixed(prof) ? 'sueldo fijo' : Utils.formatCurrency(prof.hourlyRate) + '/h'}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                </div>
                <small style="color: var(--dnr-ink-mute); display: block; margin-top: -0.5rem; margin-bottom: 1rem;">
                    <i class="fas fa-info-circle"></i> Las horas totales de la asignatura se configuran y gestionan desde la sección <a href="/plan-estudios" style="color: var(--dnr-teal-700); font-weight: 700;">Plan de Estudios</a>.
                </small>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Horario por Defecto Inicio</label>
                        <input type="time" class="form-input" name="defaultStartTime" value="${Utils.sanitizeHTML(formation.defaultStartTime || '')}">
                    </div>
                    <div>
                        <label class="form-label">Horario por Defecto Fin</label>
                        <input type="time" class="form-input" name="defaultEndTime" value="${Utils.sanitizeHTML(formation.defaultEndTime || '')}">
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Color de la Asignatura</label>
                    <input type="hidden" name="color" id="subject-color-input" value="#4169E1">
                    <div id="subject-color-picker"></div>
                    <small style="color: var(--dnr-ink-mute); display: block; margin-top: 0.5rem;">
                        Este color se usará en los calendarios para identificar esta asignatura
                    </small>
                </div>

                <div class="dnr-form-section" id="common-linking-group" style="display: block;">
                    <label class="form-label">Añadir también a otras formaciones:</label>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; max-height: 150px; overflow-y: auto; padding-right: 0.5rem;">
                        ${DataManager.getFormations()
                .filter(f => f.id !== formationId)
                .map(f => `
                                <label style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; cursor: pointer;">
                                    <input type="checkbox" name="linkedFormationIds" value="${Utils.sanitizeHTML(f.id)}">
                                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${Utils.sanitizeHTML(f.name)}">
                                        ${Utils.sanitizeHTML(f.name)}
                                    </span>
                                </label>
                            `).join('')}
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Descripción</label>
                    <textarea class="form-textarea" name="description"></textarea>
                </div>
            
            <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                    Cancelar
                </button>
                <button type="submit" class="btn btn-primary">
                    <i class="fas fa-save"></i> Guardar
                </button>
            </div>
            </form>
        `;

        Utils.openModal('Nueva Asignatura', content);

        // Initialize color picker
        setTimeout(() => {
            const colorInput = document.getElementById('subject-color-input');
            new ColorPicker('subject-color-picker', '#4169E1', (color) => {
                colorInput.value = color;
            });
        }, 0);
    },

    handleAddSubject(event, formationId) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("plan-estudios",["admin","coordinator"])) return;
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData);

        // Get linked formations (checkboxes return multiple values if we use getAll)
        const linkedFormationIds = formData.getAll('linkedFormationIds');
        const targetFormationIds = [formationId, ...linkedFormationIds];

        // Prepare the base subject data once
        const baseSubjectData = {
            name: data.name,
            type: data.type,
            totalHours: parseInt(data.totalHours) || 0,
            studyBlock: data.studyBlock || 'OTROS',
            professorId: data.professorId || null,
            defaultStartTime: data.defaultStartTime || null,
            defaultEndTime: data.defaultEndTime || null,
            color: data.color || '#FFD700',
            description: data.description || ''
        };

        // Create/Update subject for each target formation
        targetFormationIds.forEach(fId => {
            // Check if a subject with the same name already exists in this formation
            const existingSubjects = DataManager.getSubjectsByFormation(fId);
            const existing = existingSubjects.find(s => s.name.trim().toLowerCase() === baseSubjectData.name.trim().toLowerCase());

            if (existing) {
                // Update existing subject in that formation
                DataManager.updateSubject(existing.id, baseSubjectData);
            } else {
                // Create new subject
                DataManager.createSubject({ ...baseSubjectData, formationId: fId });
            }
        });

        Utils.closeModal();
        Utils.showToast(`Asignatura creada en ${targetFormationIds.length} formación(es) correctamente`, 'success');

        if (this.currentFormationId) {
            this.renderDetails(this.currentFormationId);
            App.render();
        } else {
            App.navigate('formaciones');
        }
    },

    applySubjectSuggestion(encodedData) {
        const notice = document.getElementById('continuation-applied-notice');
        if (!encodedData) {
            if (notice) notice.style.display = 'none';
            return;
        }

        try {
            const data = JSON.parse(decodeURIComponent(encodedData));

            // Set name
            const nameInput = document.getElementById('new-subject-name');
            if (nameInput) {
                nameInput.value = data.name;
                nameInput.dispatchEvent(new Event('input', { bubbles: true }));
            }

            // Set type and trigger linking group visibility
            const typeSelect = document.querySelector('#subject-form select[name="type"]');
            if (typeSelect) {
                typeSelect.value = data.type;
                if (typeof this.handleSubjectTypeChange === 'function') {
                    this.handleSubjectTypeChange(typeSelect);
                }
            }

            // Set study block
            const blockSelect = document.querySelector('#subject-form select[name="studyBlock"]');
            if (blockSelect && data.studyBlock) {
                blockSelect.value = data.studyBlock;
            }

            // Set professor if available
            if (data.professorId) {
                const profSelect = document.getElementById('new-subject-professor');
                if (profSelect) profSelect.value = data.professorId;
            }

            // Set color
            if (data.color) {
                const colorInput = document.getElementById('subject-color-input');
                if (colorInput) colorInput.value = data.color;
            }

            if (notice) {
                notice.style.display = 'flex';
                SafeHTML.set(notice, `<i class="fas fa-check-circle"></i> Vinculada como continuación de <strong>${Utils.sanitizeHTML(data.name)}</strong> (bloque: ${data.studyBlock}, tipo: ${data.type}).`);
            }
        } catch (e) {
            console.error('Error applying subject suggestion:', e);
        }
    },

    handleSubjectTypeChange(selectElement) {
        const form = selectElement.closest('form');
        const colorInputs = form.querySelectorAll('input[name="color"]');
        const professorSelect = form.querySelector('select[name="professorId"]');
        const commonLinkingGroup = form.querySelector('#common-linking-group');

        const isVAC = selectElement.value === 'VAC';
        const isCommon = selectElement.value === 'COMÚN';

        // Toggle Common Linking Group
        if (commonLinkingGroup) {
            commonLinkingGroup.style.display = isCommon ? 'block' : 'none';
        }

        if (isVAC) {
            // 1. Select RED color
            let redFound = false;
            colorInputs.forEach(input => {
                const val = input.value.toUpperCase();
                if (val === '#FF6347' || val === '#EF4444' || val === '#FA8072') {
                    input.checked = true;
                    redFound = true;
                }
            });

            // 2. Disable Professor
            if (professorSelect) {
                professorSelect.value = "";
                professorSelect.disabled = true;
            }
        } else {
            if (professorSelect) {
                professorSelect.disabled = false;
            }
        }
    },

    showEditSubjectForm(subjectId) {
        if (!AuthManager.requireModuleManage("plan-estudios",["admin","coordinator"])) return;
        const subject = DataManager.getSubject(subjectId);
        const professors = DataManager.getProfessors();
        if (!subject) return;

        // Ensure schedules array exists
        const schedules = subject.schedules || [];
        const schedulesJson = JSON.stringify(schedules).replace(/"/g, '&quot;');

        const content = `
            <form id="subject-form" class="dnr-proposal fmtd-form-dnr" ${UIEvents.attr("submit", function(event,captured){Formaciones.handleUpdateSubject(event,String(captured[0]));}, [subjectId])}>
                <input type="hidden" name="schedules" id="schedules-input" value="${schedulesJson}">

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Nombre de la Asignatura *</label>
                        <input type="text" class="form-input" name="name" value="${Utils.sanitizeHTML(subject.name)}" required>
                    </div>
                    <div>
                        <label class="form-label">Tipo *</label>
                        <select class="form-select" name="type" required ${UIEvents.attr("change", function(event,captured){Formaciones.handleSubjectTypeChange(this);}, [])}>
                            <option value="COMÚN" ${subject.type === 'COMÚN' ? 'selected' : ''}>COMÚN</option>
                            <option value="DPT" ${subject.type === 'DPT' ? 'selected' : ''}>DPT</option>
                            <option value="EXT" ${subject.type === 'EXT' ? 'selected' : ''}>EXT</option>
                            <option value="PRACT" ${subject.type === 'PRACT' ? 'selected' : ''}>PRACT</option>
                            <option value="VAC" ${subject.type === 'VAC' ? 'selected' : ''}>VAC</option>
                        </select>
                    </div>
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Bloque de Estudios</label>
                        <select class="form-select" name="studyBlock">
                            <option value="">-- Sin asignar --</option>
                            ${(() => {
                                const formation = DataManager.getFormation(subject.formationId);
                                const catBlocks = this.getBlocksForCategory(formation ? formation.type : null);
                                return catBlocks.map(b => `<option value="${Utils.sanitizeHTML(b.id)}" ${subject.studyBlock === b.id ? 'selected' : ''}>${Utils.sanitizeHTML(this.blockLabel(b))}</option>`).join('');
                            })()}
                        </select>
                    </div>
                    <div>
                        <label class="form-label">Profesor Asignado</label>
                        <select class="form-select" name="professorId" id="edit-subject-professor">
                            <option value="">Sin profesor asignado</option>
                            ${professors.map(prof => `
                                <option value="${Utils.sanitizeHTML(prof.id)}" ${prof.id === subject.professorId ? 'selected' : ''}>
                                    ${Utils.sanitizeHTML(prof.firstName)} ${Utils.sanitizeHTML(prof.lastName)} - ${StaffPay.isFixed(prof) ? 'sueldo fijo' : Utils.formatCurrency(prof.hourlyRate) + '/h'}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                </div>
                <small style="color: var(--dnr-ink-mute); display: block; margin-top: -0.5rem; margin-bottom: 1rem;">
                    <i class="fas fa-clock"></i> Horas totales configuradas: <strong style="color: var(--dnr-teal-700);">${subject.totalHours || 0}h</strong>. Para modificarlas, ve a la sección <a href="/plan-estudios" style="color: var(--dnr-teal-700); font-weight: 700;">Plan de Estudios</a>.
                </small>

                <!-- MULTI-SCHEDULE SECTION -->
                <div class="dnr-form-section">
                    <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
                        <span>Gestión de Horarios y Colores</span>
                        <span class="fmtd-type-badge is-teal">Nuevo</span>
                    </label>

                    <div id="schedules-list" style="margin-bottom: 1rem; display: flex; flex-direction: column; gap: 0.5rem;">
                        <!-- List populated by JS -->
                    </div>

                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 0.5rem; align-items: end; background: var(--dnr-surface); padding: 0.75rem; border-radius: var(--dnr-r-sm);">
                        <div>
                            <label style="font-size: 0.75rem; color: var(--dnr-ink-mute);">Nombre (Ej: Mañana)</label>
                            <input type="text" class="form-input form-sm" id="new-sched-name" placeholder="Grupo/Turno" value="Grupo Principal">
                        </div>
                        <div>
                             <label style="font-size: 0.75rem; color: var(--dnr-ink-mute);">Horario</label>
                             <div style="display: flex; gap: 2px;">
                                <input type="time" class="form-input form-sm" id="new-sched-start" value="${Utils.sanitizeHTML(subject.defaultStartTime || '')}">
                                <input type="time" class="form-input form-sm" id="new-sched-end" value="${Utils.sanitizeHTML(subject.defaultEndTime || '')}">
                             </div>
                        </div>
                        <div>
                            <label style="font-size: 0.75rem; color: var(--dnr-ink-mute);">Color</label>
                            <input type="color" class="form-input form-sm" id="new-sched-color" value="${formationDetailsColor(subject.color, '#3B82F6')}" style="height: 32px; padding: 2px;">
                        </div>
                        <button type="button" class="btn btn-sm btn-primary" ${UIEvents.attr("click", function(event,captured){Formaciones.addScheduleFromForm();}, [])}>
                            <i class="fas fa-plus"></i>
                        </button>
                    </div>
                    <p style="font-size: 0.8rem; color: var(--dnr-ink-mute); margin-top: 0.5rem;">
                        <i class="fas fa-info-circle"></i> Define múltiples horarios para asignar esta asignatura en diferentes turnos o grupos.
                    </p>
                </div>

                <div class="dnr-form-section" id="common-linking-group" style="display: ${subject.type === 'COMÚN' ? 'block' : 'none'};">
                    <label class="form-label">Añadir también a otras formaciones:</label>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; max-height: 150px; overflow-y: auto; padding-right: 0.5rem;">
                        ${DataManager.getFormations()
                .filter(f => f.id !== subject.formationId)
                .map(f => `
                                <label style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; cursor: pointer;">
                                    <input type="checkbox" name="linkedFormationIds" value="${Utils.sanitizeHTML(f.id)}">
                                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${Utils.sanitizeHTML(f.name)}">
                                        ${Utils.sanitizeHTML(f.name)}
                                    </span>
                                </label>
                            `).join('')}
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Descripción</label>
                    <textarea class="form-textarea" name="description">${Utils.sanitizeHTML(subject.description || '')}</textarea>
                </div>
                
                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-save"></i> Actualizar
                    </button>
                </div>
            </form>
        `;

        Utils.openModal('Editar Asignatura', content);

        // Initialize list
        setTimeout(() => this.renderSchedulesListInForm(), 0);
    },

    handleUpdateSubject(event, subjectId) {
        event.preventDefault();
        if (!AuthManager.requireModuleManage("plan-estudios",["admin","coordinator"])) return;
        const formData = new FormData(event.target);

        // Parse schedules
        let schedules = [];
        try {
            schedules = JSON.parse(formData.get('schedules'));
        } catch (e) {
            console.error('Error parsing schedules', e);
        }

        // Determine default values from first schedule if available, else standard fallback
        // This ensures backward compatibility
        let defaultStartTime = formData.get('defaultStartTime');
        let defaultEndTime = formData.get('defaultEndTime');
        let color = formData.get('color') || '#3B82F6';

        if (schedules.length > 0) {
            // Apply first schedule as default for legacy views
            defaultStartTime = schedules[0].startTime;
            defaultEndTime = schedules[0].endTime;
            color = schedules[0].color;
        }

        const data = Object.fromEntries(formData);
        const existingSubject = DataManager.getSubject(subjectId);

        const updateData = {
            name: data.name,
            professorId: data.professorId || null,
            description: data.description || '',
            totalHours: data.totalHours !== undefined ? (parseInt(data.totalHours) || 0) : ((existingSubject && existingSubject.totalHours) || 0),
            studyBlock: data.studyBlock || (existingSubject ? existingSubject.studyBlock : 'OTROS'),
            defaultStartTime: defaultStartTime,
            defaultEndTime: defaultEndTime,
            color: color, // Fallback main color
            schedules: schedules, // NEW: Full list
            type: data.type
        };

        DataManager.updateSubject(subjectId, updateData);

        // Handle propagation to other formations (create NEW or UPDATE existing)
        const linkedFormationIds = formData.getAll('linkedFormationIds');
        if (linkedFormationIds.length > 0) {
            linkedFormationIds.forEach(fId => {
                const existingSubjects = DataManager.getSubjectsByFormation(fId);
                const existing = existingSubjects.find(s => s.name.trim().toLowerCase() === updateData.name.trim().toLowerCase());

                if (existing) {
                    DataManager.updateSubject(existing.id, updateData);
                } else {
                    DataManager.createSubject({
                        ...updateData,
                        formationId: fId
                    });
                }
            });
        }

        Utils.closeModal();
        Utils.showToast('Asignatura actualizada correctamente', 'success');

        if (this.currentFormationId) {
            this.renderDetails(this.currentFormationId);
            App.render();
        } else {
            App.navigate('formaciones');
        }
    },

    async deleteSubject(subjectId) {
        if (!AuthManager.requireModuleManage("plan-estudios",["admin","coordinator"])) return;
        const subject = DataManager.getSubject(subjectId);
        if (!subject) return;

        // The server removes the classes of every course: ask it how many there are.
        let classCount = DataManager.getClassesBySubject(subjectId).length;
        try {
            const response = await fetch('/api/classes/summary?' + new URLSearchParams({ groupBy: 'subject', subjectId }), { headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() } });
            const body = response.ok ? await response.json() : null;
            if (Array.isArray(body?.rows)) classCount = body.rows[0]?.count || 0;
        } catch (e) { /* keep the count of the classes in memory */ }

        Utils.showConfirm(
            `¿Estás seguro de que quieres eliminar "${subject.name}"? Esto también eliminará ${classCount} clase(s) asociada(s).`,
            () => {
                DataManager.deleteSubject(subjectId);
                Utils.showToast('Asignatura eliminada correctamente', 'success');
                App.navigate(App.currentView || 'formaciones');
            }
        );
    },

    deleteClass(classId) {
        Utils.showConfirm(
            '¿Estás seguro de que quieres eliminar esta clase?',
            () => {
                DataManager.deleteClass(classId);
                Utils.showToast('Clase eliminada correctamente', 'success');
                App.navigate(App.currentView || 'formaciones');
            }
        );
    },

    showCopySubjectForm(subjectId) {
        const subject = DataManager.getSubject(subjectId);
        const professors = DataManager.getProfessors();
        if (!subject) return;

        const content = `
            <form id="subject-form" class="dnr-proposal fmtd-form-dnr" ${UIEvents.attr("submit", function(event,captured){Formaciones.handleAddSubject(event,String(captured[0]));}, [subject.formationId])}>
                <input type="hidden" name="totalHours" value="${subject.totalHours || 0}">
                <div class="alert alert-info mb-1" style="font-size: 0.9rem; padding: 0.75rem;">
                    <i class="fas fa-info-circle"></i> Estás clonando la asignatura <strong>${Utils.sanitizeHTML(subject.name)}</strong>.
                    Selecciona las formaciones de destino.
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Nombre de la Asignatura *</label>
                        <input type="text" class="form-input" name="name" value="${Utils.sanitizeHTML(subject.name)}" required>
                    </div>
                    <div>
                        <label class="form-label">Tipo *</label>
                        <select class="form-select" name="type" required ${UIEvents.attr("change", function(event,captured){Formaciones.handleSubjectTypeChange(this);}, [])}>
                            <option value="COMÚN" ${subject.type === 'COMÚN' ? 'selected' : ''}>COMÚN</option>
                            <option value="DPT" ${subject.type === 'DPT' ? 'selected' : ''}>DPT</option>
                            <option value="EXT" ${subject.type === 'EXT' ? 'selected' : ''}>EXT</option>
                            <option value="PRACT" ${subject.type === 'PRACT' ? 'selected' : ''}>PRACT</option>
                            <option value="VAC" ${subject.type === 'VAC' ? 'selected' : ''}>VAC</option>
                        </select>
                    </div>
                </div>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Bloque de Estudios</label>
                        <select class="form-select" name="studyBlock">
                            <option value="">-- Sin asignar --</option>
                            ${(() => {
                                const formation = DataManager.getFormation(subject.formationId);
                                const catBlocks = this.getBlocksForCategory(formation ? formation.type : null);
                                return catBlocks.map(b => `<option value="${Utils.sanitizeHTML(b.id)}" ${subject.studyBlock === b.id ? 'selected' : ''}>${Utils.sanitizeHTML(this.blockLabel(b))}</option>`).join('');
                            })()}
                        </select>
                    </div>
                    <div>
                        <label class="form-label">Profesor Asignado</label>
                        <select class="form-select" name="professorId" id="copy-subject-professor">
                            <option value="">Sin profesor asignado</option>
                            ${professors.map(prof => `
                                <option value="${Utils.sanitizeHTML(prof.id)}" ${prof.id === subject.professorId ? 'selected' : ''}>
                                    ${Utils.sanitizeHTML(prof.firstName)} ${Utils.sanitizeHTML(prof.lastName)} - ${StaffPay.isFixed(prof) ? 'sueldo fijo' : Utils.formatCurrency(prof.hourlyRate) + '/h'}
                                </option>
                            `).join('')}
                        </select>
                    </div>
                </div>
                <small style="color: var(--dnr-ink-mute); display: block; margin-top: -0.5rem; margin-bottom: 1rem;">
                    <i class="fas fa-clock"></i> Horas totales heredadas: <strong style="color: var(--dnr-teal-700);">${subject.totalHours || 0}h</strong>.
                </small>

                <div class="form-group grid grid-2" style="gap: 1rem;">
                    <div>
                        <label class="form-label">Horario por Defecto Inicio</label>
                        <input type="time" class="form-input" name="defaultStartTime" value="${Utils.sanitizeHTML(subject.defaultStartTime || '')}">
                    </div>
                    <div>
                        <label class="form-label">Horario por Defecto Fin</label>
                        <input type="time" class="form-input" name="defaultEndTime" value="${Utils.sanitizeHTML(subject.defaultEndTime || '')}">
                    </div>
                </div>

                <div class="dnr-form-section" id="common-linking-group" style="display: block;">
                    <label class="form-label">Seleccionar formaciones de destino:</label>
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; max-height: 150px; overflow-y: auto; padding-right: 0.5rem;">
                        ${DataManager.getFormations()
                .map(f => `
                                <label style="display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; cursor: pointer;">
                                    <input type="checkbox" name="linkedFormationIds" value="${Utils.sanitizeHTML(f.id)}" ${f.id === subject.formationId ? 'disabled checked' : ''}>
                                    <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; ${f.id === subject.formationId ? 'color: var(--dnr-ink-mute);' : ''}" title="${Utils.sanitizeHTML(f.name)}">
                                        ${Utils.sanitizeHTML(f.name)} ${f.id === subject.formationId ? '(Actual)' : ''}
                                    </span>
                                </label>
                            `).join('')}
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label">Color de la Asignatura</label>
                    <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                        ${['#FFD700', '#FF8C00', '#9370DB', '#4169E1', '#32CD32', '#FF6347', '#FF69B4', '#20B2AA', '#DDA0DD', '#F0E68C', '#87CEEB', '#FA8072'].map(color => `
                            <label class="color-picker-option">
                                <input type="radio" name="color" value="${color}" ${subject.color === color ? 'checked' : ''}>
                                <span class="color-picker-badge" style="background: ${color};"></span>
                            </label>
                        `).join('')}
                    </div>
                </div>
                
                <div class="form-group">
                    <label class="form-label">Descripción</label>
                    <textarea class="form-textarea" name="description">${Utils.sanitizeHTML(subject.description || '')}</textarea>
                </div>
                
                <div class="flex gap-1" style="justify-content: flex-end; margin-top: 1.5rem;">
                    <button type="button" class="btn btn-secondary" ${UIEvents.attr("click", function(event,captured){Utils.closeModal();}, [])}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-primary">
                        <i class="fas fa-copy"></i> Clonar Asignatura
                    </button>
                </div>
            </form>
        `;

        Utils.openModal('Clonar Asignatura', content);
    },

    // Helper to render the small list inside modal
    renderSchedulesListInForm() {
        const input = document.getElementById('schedules-input');
        const list = document.getElementById('schedules-list');
        if (!input || !list) return;

        let schedules = [];
        try {
            schedules = JSON.parse(input.value);
        } catch (e) { schedules = []; }

        if (schedules.length === 0) {
            SafeHTML.set(list, '<p style="text-align: center; font-size: 0.85rem; color: var(--dnr-ink-mute); font-style: italic;">No hay horarios definidos.</p>');
            return;
        }

        SafeHTML.set(list, schedules.map((s, index) => `
            <div class="fmtd-schedule-item" style="border-left: 4px solid ${formationDetailsColor(s.color, '#4169E1')};">
                <div>
                    <strong style="font-size: 0.9rem; color: var(--dnr-ink);">${Utils.sanitizeHTML(s.name || 'Sin nombre')}</strong>
                    <div style="font-size: 0.8rem; color: var(--dnr-ink-mute);">
                        ${Utils.sanitizeHTML(s.startTime)} - ${Utils.sanitizeHTML(s.endTime)}
                    </div>
                </div>
                <button type="button" class="fmtd-action-btn is-danger" ${UIEvents.attr("click", function(event,captured){Formaciones.removeScheduleFromForm(captured[0]);}, [index])}>
                    <i class="fas fa-times"></i>
                </button>
            </div>
        `).join(''));
    },

    addScheduleFromForm() {
        try {
            console.log('Attempts to add schedule...');
            const nameInput = document.getElementById('new-sched-name');
            const startInput = document.getElementById('new-sched-start');
            const endInput = document.getElementById('new-sched-end');
            const colorInput = document.getElementById('new-sched-color');
            const hiddenInput = document.getElementById('schedules-input');

            if (!nameInput || !startInput || !endInput) {
                console.error('Inputs not found');
                return;
            }

            console.log('Values:', nameInput.value, startInput.value, endInput.value);

            if (!nameInput.value || !startInput.value || !endInput.value) {
                Utils.showToast('Por favor completa horario y nombre', 'warning');
                return;
            }

            let schedules = [];
            try {
                schedules = JSON.parse(hiddenInput.value || '[]');
            } catch (e) {
                console.error('Error parsing schedules JSON:', e);
                schedules = [];
            }

            const newId = (typeof Utils.generateUUID === 'function') ? Utils.generateUUID() : Date.now().toString();

            schedules.push({
                id: newId,
                name: nameInput.value,
                startTime: startInput.value,
                endTime: endInput.value,
                color: colorInput.value
            });

            hiddenInput.value = JSON.stringify(schedules);
            this.renderSchedulesListInForm();

            // Clear inputs
            // nameInput.value = ''; // Keep name for convenience? No, clear it.
            // startInput.value = '';
            // endInput.value = '';

            Utils.showToast('Horario añadido', 'success');

        } catch (error) {
            console.error('Error adding schedule:', error);
            alert('Error añadiendo horario: ' + error.message);
        }
    },

    removeScheduleFromForm(index) {
        const hiddenInput = document.getElementById('schedules-input');
        let schedules = JSON.parse(hiddenInput.value || '[]');
        schedules.splice(index, 1);
        hiddenInput.value = JSON.stringify(schedules);
        this.renderSchedulesListInForm();
    }
});
